import { useEffect, useState } from "react";
import { Bell, Mail, MessageSquare, Send, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import {
  NOTIFICATION_EVENT_KEY_LABEL,
  NOTIFICATION_EVENT_KEYS,
  type NotificationEventKey,
} from "@colab/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { NotificationRuleRow } from "@/features/settings/use-notification-rules";
import type { SegmentRow } from "@/features/settings/use-segments";
import { useUpdateNotificationRule } from "@/features/settings/use-update-notification-rule";
import { useDeleteNotificationRule } from "@/features/settings/use-delete-notification-rule";
import { useTestSendNotification } from "@/features/settings/use-test-send-notification";

// SPA port of /app/admin/settings/notifications/_components/NotificationRulesTable.tsx.
// Each row inline-edits via useUpdateNotificationRule; system rules accept
// the toggle pair (mail/chat/active) but resist delete.

const NO_SEGMENT = "__none";

type Props = {
  rules: NotificationRuleRow[];
  segments: SegmentRow[];
  canManage: boolean;
};

export function NotificationRulesTable({ rules, segments, canManage }: Props) {
  // Group rules by event_key in canonical event order.
  const grouped = groupByEvent(rules);

  return (
    <div className="space-y-4">
      {grouped.map((group) => (
        <Card key={group.eventKey}>
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  {NOTIFICATION_EVENT_KEY_LABEL[group.eventKey]}
                </div>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  {group.eventKey}
                </p>
              </div>
            </div>
            <div className="divide-y">
              {group.rules.map((rule) => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  segments={segments}
                  canManage={canManage}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RuleRow({
  rule,
  segments,
  canManage,
}: {
  rule: NotificationRuleRow;
  segments: SegmentRow[];
  canManage: boolean;
}) {
  const updateMutation = useUpdateNotificationRule();
  const deleteMutation = useDeleteNotificationRule();
  const testMutation = useTestSendNotification();

  const [draft, setDraft] = useState({
    active: rule.active,
    mail_enabled: rule.mail_enabled,
    chat_enabled: rule.chat_enabled,
    include_natural_recipient: rule.include_natural_recipient,
    audience_segment_id: rule.audience_segment_id,
    mail_subject_override: rule.mail_subject_override ?? "",
  });

  useEffect(() => {
    setDraft({
      active: rule.active,
      mail_enabled: rule.mail_enabled,
      chat_enabled: rule.chat_enabled,
      include_natural_recipient: rule.include_natural_recipient,
      audience_segment_id: rule.audience_segment_id,
      mail_subject_override: rule.mail_subject_override ?? "",
    });
  }, [rule]);

  const pending = updateMutation.isPending || deleteMutation.isPending;

  function persist(next: Partial<typeof draft>) {
    if (!canManage) return;
    setDraft((cur) => ({ ...cur, ...next }));
    const patch: Record<string, unknown> = { ...next };
    if ("mail_subject_override" in next) {
      patch.mail_subject_override =
        String(next.mail_subject_override ?? "").trim() || null;
    }
    updateMutation.mutate(
      { ruleId: rule.id, patch },
      {
        onError: () => {
          // Roll back the optimistic patch to the row's canonical value.
          setDraft((cur) => ({
            ...cur,
            active: rule.active,
            mail_enabled: rule.mail_enabled,
            chat_enabled: rule.chat_enabled,
            include_natural_recipient: rule.include_natural_recipient,
            audience_segment_id: rule.audience_segment_id,
            mail_subject_override: rule.mail_subject_override ?? "",
          }));
        },
      },
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <Switch
            checked={draft.active}
            disabled={!canManage || pending}
            onCheckedChange={(v) => persist({ active: v })}
          />
          <span className="font-medium">{rule.name}</span>
          {rule.is_system ? (
            <Badge variant="outline" className="font-mono text-[9px] uppercase">
              system
            </Badge>
          ) : null}
        </div>

        <div className="ml-auto flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            Mail
            <Switch
              checked={draft.mail_enabled}
              disabled={!canManage || pending}
              onCheckedChange={(v) => persist({ mail_enabled: v })}
            />
          </label>
          <label className="flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
            Chat
            <Switch
              checked={draft.chat_enabled}
              disabled={!canManage || pending}
              onCheckedChange={(v) => persist({ chat_enabled: v })}
            />
          </label>

          {canManage ? (
            <Button
              size="sm"
              variant="outline"
              disabled={pending || testMutation.isPending}
              onClick={() => {
                if (!isNotificationEventKey(rule.event_key)) {
                  toast.error("Unknown event key — cannot fire a test send.");
                  return;
                }
                testMutation.mutate(rule.event_key as NotificationEventKey);
              }}
            >
              <Send className="mr-1 h-3 w-3" />
              Test send
            </Button>
          ) : null}

          {canManage && !rule.is_system ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Future events of this type will fall back to other active
                    rules (or fire nothing if none remain).
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteMutation.mutate(rule.id)}
                  >
                    Delete rule
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </div>
      </div>

      {rule.description ? (
        <p className="mt-1 text-xs text-muted-foreground">{rule.description}</p>
      ) : null}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-xs">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Users className="h-3 w-3" />
            Audience
          </span>
          <Select
            value={draft.audience_segment_id ?? NO_SEGMENT}
            disabled={!canManage || pending}
            onValueChange={(v) =>
              persist({ audience_segment_id: v === NO_SEGMENT ? null : v })
            }
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="All partners (broadcast)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_SEGMENT}>
                All partners (no segment filter)
              </SelectItem>
              {segments.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Subject line override</span>
          <Input
            className="h-8 text-xs"
            placeholder="Use the default template subject"
            value={draft.mail_subject_override}
            disabled={!canManage || pending}
            onChange={(e) =>
              setDraft((cur) => ({
                ...cur,
                mail_subject_override: e.target.value,
              }))
            }
            onBlur={(e) =>
              persist({ mail_subject_override: e.target.value })
            }
          />
        </label>
      </div>

      <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Switch
          checked={draft.include_natural_recipient}
          disabled={!canManage || pending}
          onCheckedChange={(v) => persist({ include_natural_recipient: v })}
        />
        Also notify the event&apos;s natural recipient (partner primary
        contact, submission submitter, invitee)
      </label>
    </div>
  );
}

type Group = {
  eventKey: NotificationEventKey;
  rules: NotificationRuleRow[];
};

function groupByEvent(rules: NotificationRuleRow[]): Group[] {
  const map = new Map<NotificationEventKey, NotificationRuleRow[]>();
  for (const r of rules) {
    if (!isNotificationEventKey(r.event_key)) continue;
    const key = r.event_key as NotificationEventKey;
    const list = map.get(key) ?? [];
    list.push(r);
    map.set(key, list);
  }
  // Walk NOTIFICATION_EVENT_KEYS to preserve the canonical event order.
  const ordered: Group[] = [];
  for (const k of NOTIFICATION_EVENT_KEYS) {
    const rs = map.get(k);
    if (rs && rs.length > 0) ordered.push({ eventKey: k, rules: rs });
  }
  return ordered;
}

function isNotificationEventKey(value: string): value is NotificationEventKey {
  return (NOTIFICATION_EVENT_KEYS as readonly string[]).includes(value);
}
